import { useMemo, useState } from 'react';
import { realLinkProps } from '../../components/links/realLinkProps';
import { useRouterNavigate } from '../../components/links/useEntityLinkProps';
import { useTranslation } from 'react-i18next';
import {
  Text,
  Button,
  Spinner,
  Link,
  MessageBar,
  MessageBarBody,
  Badge,
  Input,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  RadioGroup,
  Radio,
  Textarea,
  Field,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, HistoryRegular, MoreHorizontalRegular } from '@fluentui/react-icons';
import {
  useClientStateTaxObligations,
  useIsStateTaxAdmin,
  useSuggestedObligations,
  useObligationFrequencyChanges,
  useRecordFrequencyChange,
  useRemoveClientStateTaxObligation,
  useEndClientStateTaxObligation,
  useReactivateClientStateTaxObligation,
  latestFrequencyChangeByType,
  type RemovedObligationOpenTask,
  type EndedObligationTask,
  type EndObligationPayload,
} from '../../services/stateTaxClientsApi';
import { formatReportingSince } from '../../utils/obligationHelpers';
import { formatWholeDay } from '../../utils/complianceFormatters';
import AddStateTaxObligationDialog from '../../components/state-tax/AddStateTaxObligationDialog';
import { CatchupGenerationDialog } from './CatchupGenerationDialog';
import { FilingHistoryStrip, FilingHistoryLegend } from './FilingHistoryStrip';
import { ObligationHistoryPopover } from './ObligationHistoryPopover';
import { OBLIGATION_END_REASONS, type ObligationEndReason } from '../../utils/obligationEndReasons';
import SurtaxEditDialog from '../../components/compliance/SurtaxEditDialog';
import ObligationSurtaxLine from '../../components/compliance/ObligationSurtaxLine';
import type { ObligationFrequencyChange, StateTaxObligationSummary, SuggestedObligation } from '../../types';
import { CalendarDateInput } from '../../components/dates/CalendarDateInput';
import { cadenceName } from '../../utils/cadenceName';

// Today as a LOCAL yyyy-MM-dd (not toISOString(), which is UTC and can roll to tomorrow for a
// US-evening user — the End default date must match the calendar the staffer sees).
function todayLocalIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * State-tax obligations table for one client. Owns the
 * AddStateTaxObligationDialog mount state — opened from three callbacks
 * (Add / Edit / Add-from-suggestion). SuggestionsCard + ObligationRow
 * stay in this file because they share that state contract.
 */
const useStyles = makeStyles({
  // minWidth:0 lets this card shrink to its cardGrid track instead of being forced
  // wide by the obligations table's intrinsic width (grid items default to
  // min-width:auto, which is what let the wide table bleed into the next card).
  card: { width: '100%', minWidth: 0 },
  // Hard containment for the wide obligations table: it scrolls horizontally WITHIN
  // the card rather than overflowing into neighbouring cards.
  tableScroll: { overflowX: 'auto', maxWidth: '100%' },
  openTaskList: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: 0,
    paddingLeft: tokens.spacingHorizontalXXL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  notesCell: {
    whiteSpace: 'pre-wrap',
    maxWidth: '220px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  derivedRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  derivedFootnote: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    marginTop: tokens.spacingVerticalS,
  },
  // F2 — wraps the Active/Inactive Badge in an unstyled button so it works as
  // a PopoverTrigger without the default browser button chrome interfering
  // with the Badge's own styling.
  statusBadgeButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  // Yellow-tinted "we noticed something" card surfaced above the obligations
  // table when the API returns derived suggestions.
  suggestionsCard: {
    width: '100%',
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderTop: `1px solid ${tokens.colorPaletteYellowBorder2}`,
  },
  suggestionItemFirst: {
    borderTop: 'none',
  },
  suggestionMeta: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  suggestionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  suggestionDetails: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  suggestionFiles: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
  },
});

export function ObligationsCard({
  stateTaxClientId,
  defaultInformationSource,
  clientName,
}: {
  stateTaxClientId: string;
  defaultInformationSource?: string;
  /** Client's display name (Dataverse account name) — threaded into FilingHistoryStrip's
   * RecordManualFilingDialog for the Flow C name-mismatch warning. */
  clientName?: string;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const dash = t('common.dash');
  const { data, isLoading, isError } = useClientStateTaxObligations(stateTaxClientId);
  const { data: freqChanges } = useObligationFrequencyChanges(stateTaxClientId);
  const latestFreqByType = latestFrequencyChangeByType(freqChanges);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Keyed by filingObligationId, NEVER the row's own id: this list lets the Dataverse row win a
  // tax type while generation lets the Cosmos twin win it (see FilingObligationIdTests).
  const [surtaxTarget, setSurtaxTarget] = useState<StateTaxObligationSummary | null>(null);
  const [editTarget, setEditTarget] = useState<StateTaxObligationSummary | undefined>(undefined);
  const [suggestionTarget, setSuggestionTarget] = useState<SuggestedObligation | undefined>(undefined);
  // Remove-obligation confirm flow (spec 2026-07-14). The pending row drives the
  // confirm dialog; errors render inline in the dialog so the staffer can retry.
  const [removeTarget, setRemoveTarget] = useState<StateTaxObligationSummary | undefined>(undefined);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // Non-null after a successful removal that left open tasks still carrying an unfiled
  // filing of the removed type — removal never cancels tasks, so those keep sending
  // client reminders until someone reviews them (the Venemex Pro incident, 2026-07-14).
  const [removeOpenTasks, setRemoveOpenTasks] = useState<RemovedObligationOpenTask[] | null>(null);
  const removeMutation = useRemoveClientStateTaxObligation();
  // End / cancel with an effective date (spec 2026-07-15). Distinct from remove: preserves the
  // obligation, stops future periods after the date, and cancels open future tasks.
  const [endTarget, setEndTarget] = useState<StateTaxObligationSummary | undefined>(undefined);
  const [endDate, setEndDate] = useState<string>(() => todayLocalIso());
  const [endStatus, setEndStatus] = useState<'ended' | 'cancelled'>('ended');
  const [endReason, setEndReason] = useState('');
  // The coded reason — required. Starts unset so nobody's end is recorded under a default they
  // never chose (fail-closed, like the server).
  const [endReasonCode, setEndReasonCode] = useState<ObligationEndReason | ''>('');
  const [endError, setEndError] = useState<string | null>(null);
  const [endDone, setEndDone] = useState<{ cancelled: EndedObligationTask[]; review: EndedObligationTask[] } | null>(null);
  const endMutation = useEndClientStateTaxObligation();
  // Reactivate / un-end (spec 2026-07-15). Reverses an end/cancel: clears the effective end date so
  // future generation resumes. Deliberately does NOT reopen the tasks the end cancelled.
  const [reactivateTarget, setReactivateTarget] = useState<StateTaxObligationSummary | undefined>(undefined);
  const [reactivateError, setReactivateError] = useState<string | null>(null);
  const reactivateMutation = useReactivateClientStateTaxObligation();
  const linkTo = useRouterNavigate();

  // STX-02 — admin-only catch-up / historical generation. "Admin" is the SERVER's answer
  // (StaffAdminGate, via /state-tax/users/me/admin-status) — the same gate generate-catchup
  // enforces (#37) — never a match against the staff roster, which lists preparers.
  const { data: isAdmin = false } = useIsStateTaxAdmin();
  const [catchupOpen, setCatchupOpen] = useState(false);

  // Stable reference so hooks below can depend on it without re-running every render.
  const obligations = useMemo(() => data ?? [], [data]);
  const hasDerived = obligations.some(o => o.source === 'derived');

  // Default catch-up range start: earliest obligation effective-start, floored at
  // 2026-01-01 (the app's history floor) — the same rule the backend applies, so the
  // confirm dialog's pre-filled date matches the server default.
  const defaultCatchupFrom = useMemo(() => {
    const floor = '2026-01-01';
    const starts = obligations
      .map((o) => o.effectiveStartDate?.slice(0, 10))
      .filter((s): s is string => !!s);
    const earliest = starts.length > 0 ? [...starts].sort()[0] : undefined;
    return earliest && earliest > floor ? earliest : floor;
  }, [obligations]);

  function openAdd() {
    setEditTarget(undefined);
    setSuggestionTarget(undefined);
    setDialogOpen(true);
  }
  function openEdit(o: StateTaxObligationSummary) {
    setEditTarget(o);
    setSuggestionTarget(undefined);
    setDialogOpen(true);
  }
  function openFromSuggestion(s: SuggestedObligation) {
    setEditTarget(undefined);
    setSuggestionTarget(s);
    setDialogOpen(true);
  }
  function openRemove(o: StateTaxObligationSummary) {
    setRemoveError(null);
    setRemoveTarget(o);
  }
  function closeRemove() {
    setRemoveTarget(undefined);
    setRemoveError(null);
    setRemoveOpenTasks(null);
  }
  function confirmRemove() {
    const target = removeTarget;
    if (!target) return;
    setRemoveError(null);
    removeMutation.mutate(
      { stateTaxClientId, obligationId: target.id },
      {
        onSuccess: (result) => {
          // Removal succeeded either way; keep the dialog open only when there are
          // open tasks the staffer should review (and possibly cancel).
          const openTasks = result?.openTasks ?? [];
          if (openTasks.length > 0) setRemoveOpenTasks(openTasks);
          else closeRemove();
        },
        onError: (err) => {
          // Generic copy first; append the server message when it has one.
          const serverMsg = err instanceof Error && err.message ? ` ${err.message}` : '';
          setRemoveError(`${t('clientDetailPage.obligations.removeFailed')}${serverMsg}`);
        },
      },
    );
  }

  function openEnd(o: StateTaxObligationSummary) {
    setEndError(null);
    setEndDone(null);
    setEndStatus('ended');
    setEndReason('');
    setEndReasonCode('');
    setEndDate(todayLocalIso());
    setEndTarget(o);
  }
  function closeEnd() {
    setEndTarget(undefined);
    setEndError(null);
    setEndDone(null);
  }
  function confirmEnd() {
    const target = endTarget;
    if (!target || !endDate || !endReasonCode) return;
    setEndError(null);
    const payload: EndObligationPayload = {
      effectiveEndDate: endDate,
      status: endStatus,
      endReason: endReasonCode,
      reason: endReason.trim() || undefined,
    };
    endMutation.mutate(
      { stateTaxClientId, obligationId: target.id, payload },
      {
        onSuccess: (result) => {
          setEndDone({ cancelled: result.cancelledTasks, review: result.reviewTasks });
        },
        onError: (err) => {
          const serverMsg = err instanceof Error && err.message ? ` ${err.message}` : '';
          setEndError(`${t('clientDetailPage.obligations.endFailed')}${serverMsg}`);
        },
      },
    );
  }

  function openReactivate(o: StateTaxObligationSummary) {
    setReactivateError(null);
    setReactivateTarget(o);
  }
  function closeReactivate() {
    setReactivateTarget(undefined);
    setReactivateError(null);
  }
  function confirmReactivate() {
    const target = reactivateTarget;
    if (!target) return;
    setReactivateError(null);
    reactivateMutation.mutate(
      { stateTaxClientId, obligationId: target.id },
      {
        onSuccess: () => closeReactivate(),
        onError: (err) => {
          const serverMsg = err instanceof Error && err.message ? ` ${err.message}` : '';
          setReactivateError(`${t('clientDetailPage.obligations.reactivateFailed')}${serverMsg}`);
        },
      },
    );
  }

  return (
    <>
      <SuggestionsCard
        stateTaxClientId={stateTaxClientId}
        onAddFromSuggestion={openFromSuggestion}
      />
      <div className={`card ${styles.card}`}>
        <div className={styles.header}>
          <Text className={styles.heading}>
            {t('clientDetailPage.sections.stateTaxObligations')}
          </Text>
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' }}>
            {isAdmin && (
              <Button
                appearance="secondary"
                size="small"
                icon={<HistoryRegular />}
                onClick={() => setCatchupOpen(true)}
              >
                {t('clientDetailPage.obligations.catchup.button')}
              </Button>
            )}
            <Button
              appearance="primary"
              size="small"
              icon={<AddRegular />}
              onClick={openAdd}
            >
              {t('clientDetailPage.actions.addObligation')}
            </Button>
          </div>
        </div>

        {isLoading && <Spinner size="tiny" label={t('clientDetailPage.obligations.loading')} />}

        {isError && (
          <MessageBar intent="error">
            <MessageBarBody>{t('clientDetailPage.obligations.loadError')}</MessageBarBody>
          </MessageBar>
        )}

        {!isLoading && !isError && obligations.length === 0 && (
          <Text className={styles.emptyHint}>{t('clientDetailPage.obligations.empty')}</Text>
        )}

        {!isLoading && !isError && obligations.length > 0 && (
          <>
            <div className={styles.tableScroll}>
            <Table size="small" aria-label={t('clientDetailPage.sections.stateTaxObligations')}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.taxType')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.state')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.frequency')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.informationSource')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.access')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.reportingSince')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.history')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.notes')}</TableHeaderCell>
                  <TableHeaderCell>{t('clientDetailPage.obligations.columns.status')}</TableHeaderCell>
                  {/* Remove-action column — header intentionally empty (icon-only cells). */}
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {obligations.map(o => (
                  <ObligationRow
                    key={o.id}
                    obligation={o}
                    stateTaxClientId={stateTaxClientId}
                    clientName={clientName}
                    dash={dash}
                    frequencyChange={latestFreqByType[o.taxType]}
                    onClick={() => openEdit(o)}
                    onRemove={o.source !== 'derived' ? () => openRemove(o) : undefined}
                    onEnd={o.source !== 'derived' ? () => openEnd(o) : undefined}
                    onReactivate={
                      o.source !== 'derived' && o.effectiveEndDate ? () => openReactivate(o) : undefined
                    }
                    // Offered only where ONE obligation generates this row's filings. Derived
                    // rows qualify too when a seeded twin exists — the common production case.
                    onEditSurtax={
                      o.hasCountySurtax && o.filingObligationId ? () => setSurtaxTarget(o) : undefined
                    }
                  />
                ))}
              </TableBody>
            </Table>
            </div>
            <FilingHistoryLegend />
            {hasDerived && (
              <Text className={styles.derivedFootnote}>
                {t('clientDetailPage.obligations.derivedNote')}
              </Text>
            )}
          </>
        )}

        {/* Remove-obligation confirm dialog (cancel-task pattern). Copy states the exact
            consequences: future-only, filed history and existing tasks untouched. */}
        <Dialog
          open={removeTarget !== undefined}
          onOpenChange={(_e, d) => { if (!removeMutation.isPending && !d.open) closeRemove(); }}
        >
          <DialogSurface>
            {removeOpenTasks !== null ? (
              <DialogBody>
                <DialogTitle>{t('clientDetailPage.obligations.removeDoneTitle')}</DialogTitle>
                <DialogContent>
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      {t('clientDetailPage.obligations.removeOpenTasksWarning', {
                        count: removeOpenTasks.length,
                        taxType: removeTarget?.taxType ?? '',
                      })}
                    </MessageBarBody>
                  </MessageBar>
                  <ul className={styles.openTaskList}>
                    {removeOpenTasks.map((task) => (
                      <li key={task.id}>
                        <Link
                          {...realLinkProps(`/compliance/tasks/${task.id}?clientId=${encodeURIComponent(stateTaxClientId)}`, { navigate: linkTo })}
                        >
                          {task.periodLabel}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </DialogContent>
                <DialogActions>
                  <Button appearance="primary" onClick={closeRemove}>
                    {t('clientDetailPage.obligations.removeOpenTasksClose')}
                  </Button>
                </DialogActions>
              </DialogBody>
            ) : (
              <DialogBody>
                <DialogTitle>{t('clientDetailPage.obligations.removeTitle')}</DialogTitle>
                <DialogContent>
                  {t('clientDetailPage.obligations.removeBody', { taxType: removeTarget?.taxType ?? '' })}
                  {removeError && (
                    <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
                      <MessageBarBody>{removeError}</MessageBarBody>
                    </MessageBar>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    appearance="secondary"
                    disabled={removeMutation.isPending}
                    onClick={closeRemove}
                  >
                    {t('clientDetailPage.obligations.removeKeep')}
                  </Button>
                  <Button
                    appearance="primary"
                    disabled={removeMutation.isPending}
                    onClick={confirmRemove}
                  >
                    {t('clientDetailPage.obligations.removeConfirm')}
                  </Button>
                </DialogActions>
              </DialogBody>
            )}
          </DialogSurface>
        </Dialog>

        {/* End / cancel with an effective date. Two-phase like the remove dialog: the input
            form, then a done summary listing auto-cancelled + review-needed future tasks. */}
        <Dialog
          open={endTarget !== undefined}
          onOpenChange={(_e, d) => { if (!endMutation.isPending && !d.open) closeEnd(); }}
        >
          <DialogSurface>
            {endDone !== null ? (
              <DialogBody>
                <DialogTitle>{t('clientDetailPage.obligations.endDoneTitle')}</DialogTitle>
                <DialogContent>
                  <Text>
                    {t('clientDetailPage.obligations.endDoneSummary', {
                      count: endDone.cancelled.length,
                      taxType: endTarget?.taxType ?? '',
                    })}
                  </Text>
                  {endDone.review.length > 0 && (
                    <>
                      <MessageBar intent="warning" style={{ marginTop: tokens.spacingVerticalS }}>
                        <MessageBarBody>
                          {t('clientDetailPage.obligations.endReviewWarning', { count: endDone.review.length })}
                        </MessageBarBody>
                      </MessageBar>
                      <ul className={styles.openTaskList}>
                        {endDone.review.map((task) => (
                          <li key={task.id}>
                            <Link
                              {...realLinkProps(`/compliance/tasks/${task.id}?clientId=${encodeURIComponent(stateTaxClientId)}`, { navigate: linkTo })}
                            >
                              {task.periodLabel}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button appearance="primary" onClick={closeEnd}>
                    {t('clientDetailPage.obligations.endDoneClose')}
                  </Button>
                </DialogActions>
              </DialogBody>
            ) : (
              <DialogBody>
                <DialogTitle>{t('clientDetailPage.obligations.endTitle', { taxType: endTarget?.taxType ?? '' })}</DialogTitle>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {t('clientDetailPage.obligations.endBody')}
                    </Text>
                    <Field label={t('clientDetailPage.obligations.endStatusLabel')}>
                      <RadioGroup
                        value={endStatus}
                        onChange={(_e, d) => setEndStatus(d.value as 'ended' | 'cancelled')}
                      >
                        <Radio value="ended" label={t('clientDetailPage.obligations.endStatusEnded')} />
                        <Radio value="cancelled" label={t('clientDetailPage.obligations.endStatusCancelled')} />
                      </RadioGroup>
                    </Field>
                    <Field label={t('clientDetailPage.obligations.endEffectiveDate')}>
                      <CalendarDateInput value={endDate} onChange={setEndDate} />
                    </Field>
                    {/* A RadioGroup, not a Dropdown: nothing to portal (the B3 flake family), and
                        all six reasons stay visible so nobody picks the first one by default. */}
                    <Field label={t('clientDetailPage.obligations.endReasonCodeLabel')} required>
                      <RadioGroup
                        value={endReasonCode}
                        onChange={(_e, d) => setEndReasonCode(d.value as ObligationEndReason)}
                      >
                        {OBLIGATION_END_REASONS.map((code) => (
                          <Radio
                            key={code}
                            value={code}
                            label={t(`clientDetailPage.obligations.endReasonCodes.${code}`)}
                          />
                        ))}
                      </RadioGroup>
                    </Field>
                    <Field label={t('clientDetailPage.obligations.endReasonLabel')}>
                      <Textarea
                        value={endReason}
                        onChange={(_e, d) => setEndReason(d.value)}
                        placeholder={t('clientDetailPage.obligations.endReasonPlaceholder')}
                      />
                    </Field>
                  </div>
                  {endError && (
                    <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
                      <MessageBarBody>{endError}</MessageBarBody>
                    </MessageBar>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" disabled={endMutation.isPending} onClick={closeEnd}>
                    {t('clientDetailPage.obligations.endCancel')}
                  </Button>
                  <Button
                    appearance="primary"
                    disabled={endMutation.isPending || !endDate || !endReasonCode}
                    onClick={confirmEnd}
                  >
                    {t('clientDetailPage.obligations.endConfirm')}
                  </Button>
                </DialogActions>
              </DialogBody>
            )}
          </DialogSurface>
        </Dialog>

        {/* Reactivate / un-end confirm dialog. Copy notes that the previously-cancelled tasks stay
            cancelled — reactivation only resumes FUTURE generation; staff reopen tasks individually. */}
        <Dialog
          open={reactivateTarget !== undefined}
          onOpenChange={(_e, d) => { if (!reactivateMutation.isPending && !d.open) closeReactivate(); }}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                {t('clientDetailPage.obligations.reactivateTitle', { taxType: reactivateTarget?.taxType ?? '' })}
              </DialogTitle>
              <DialogContent>
                {t('clientDetailPage.obligations.reactivateBody')}
                {reactivateError && (
                  <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
                    <MessageBarBody>{reactivateError}</MessageBarBody>
                  </MessageBar>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  disabled={reactivateMutation.isPending}
                  onClick={closeReactivate}
                >
                  {t('clientDetailPage.obligations.reactivateCancel')}
                </Button>
                <Button
                  appearance="primary"
                  disabled={reactivateMutation.isPending}
                  onClick={confirmReactivate}
                >
                  {t('clientDetailPage.obligations.reactivateConfirm')}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {/* STX-02 admin catch-up dialog. Keyed remount on open so the from-date input
            re-seeds from the (possibly re-computed) default each time it opens. */}
        {catchupOpen && (
          <CatchupGenerationDialog
            open={catchupOpen}
            onOpenChange={setCatchupOpen}
            stateTaxClientId={stateTaxClientId}
            defaultFromDate={defaultCatchupFrom}
          />
        )}

        {surtaxTarget?.filingObligationId && (
          <SurtaxEditDialog
            open
            onOpenChange={(o) => { if (!o) setSurtaxTarget(null); }}
            clientId={stateTaxClientId}
            obligationId={surtaxTarget.filingObligationId}
            obligationLabel={
              surtaxTarget.filingFrequencyLabel
                ? `${surtaxTarget.taxType} (${surtaxTarget.filingFrequencyLabel ? cadenceName(t, surtaxTarget.filingFrequencyLabel) : ''})`
                : surtaxTarget.taxType
            }
            clientObligationLabels={obligations.map((o) => o.taxType)}
          />
        )}

        <AddStateTaxObligationDialog
          open={dialogOpen}
          onOpenChange={(o) => {
            setDialogOpen(o);
            if (!o) {
              setEditTarget(undefined);
              setSuggestionTarget(undefined);
            }
          }}
          stateTaxClientId={stateTaxClientId}
          obligation={editTarget}
          suggestion={suggestionTarget}
          defaultInformationSource={defaultInformationSource}
        />
      </div>
    </>
  );
}

function SuggestionsCard({
  stateTaxClientId,
  onAddFromSuggestion,
}: {
  stateTaxClientId: string;
  onAddFromSuggestion: (s: SuggestedObligation) => void;
}) {
  const { t } = useTranslation();
  const styles = useStyles();
  const { data, isLoading } = useSuggestedObligations(stateTaxClientId);
  const suggestions = data ?? [];

  if (isLoading || suggestions.length === 0) return null;

  return (
    <div className={`card ${styles.suggestionsCard}`}>
      <Text className={styles.heading} style={{ marginBottom: 0, paddingBottom: 0, border: 'none' }}>
        {t('clientDetailPage.suggestions.title')}
      </Text>
      <Text className={styles.suggestionDetails} style={{ marginBottom: '8px' }}>
        {t('clientDetailPage.suggestions.intro')}
      </Text>
      {suggestions.map((s, idx) => (
        <div
          key={s.taxType}
          className={`${styles.suggestionItem} ${idx === 0 ? styles.suggestionItemFirst : ''}`}
        >
          <div className={styles.suggestionMeta}>
            <Text className={styles.suggestionTitle}>{s.taxType}</Text>
            <Text className={styles.suggestionDetails}>
              {t('clientDetailPage.suggestions.files', { count: s.fileCount })} · {t('clientDetailPage.suggestions.suggestedFrequency', { frequency: s.suggestedFilingFrequency.toLowerCase() })}
              {s.latestFilingDate ? ` · ${t('clientDetailPage.suggestions.latest', { date: s.latestFilingDate.slice(0, 10) })}` : ''}
            </Text>
            {s.sampleFilenames.length > 0 && (
              <Text className={styles.suggestionFiles}>{t('clientDetailPage.suggestions.examples', { files: s.sampleFilenames.slice(0, 2).join(', ') })}</Text>
            )}
          </div>
          <Button
            appearance="primary"
            size="small"
            icon={<AddRegular />}
            onClick={() => onAddFromSuggestion(s)}
          >
            {t('clientDetailPage.suggestions.add')}
          </Button>
        </div>
      ))}
    </div>
  );
}

function ObligationRow({
  obligation,
  stateTaxClientId,
  clientName,
  dash,
  frequencyChange,
  onClick,
  onRemove,
  onEnd,
  onReactivate,
  onEditSurtax,
}: {
  obligation: StateTaxObligationSummary;
  stateTaxClientId: string;
  /** Client's display name — threaded into FilingHistoryStrip's RecordManualFilingDialog
   * for the Flow C name-mismatch warning. */
  clientName?: string;
  dash: string;
  frequencyChange?: ObligationFrequencyChange;
  onClick?: () => void;
  /** Remove affordance — omitted for derived rows (synthesized, no backing record). */
  onRemove?: () => void;
  /** End/cancel-with-date affordance — omitted for derived rows. */
  onEnd?: () => void;
  /** Reactivate (reverse an end/cancel) — present only for ended/cancelled rows. */
  onReactivate?: () => void;
  /** Edit surtax — present only for a FL DR-15 row with a single filing obligation. */
  onEditSurtax?: () => void;
}) {
  const { t } = useTranslation();
  const styles = useStyles();
  const recordMutation = useRecordFrequencyChange();

  // Record-frequency-change popover state. Default the effective date to Jan 1 of the
  // current year — the common "switched at the year boundary, caught late" case.
  const [recordOpen, setRecordOpen] = useState(false);
  const defaultEffective = `${new Date().getFullYear()}-01-01`;
  const [effectiveDate, setEffectiveDate] = useState(defaultEffective);
  const [note, setNote] = useState('');

  // The mismatch badge is "acknowledged" once a recorded change matches the observed cadence.
  const acknowledged = !!frequencyChange && frequencyChange.frequency === obligation.cadenceMismatch;
  const showMismatch = !!obligation.cadenceMismatch && !acknowledged;

  function submitRecord() {
    if (!obligation.cadenceMismatch) return;
    recordMutation.mutate(
      {
        stateTaxClientId,
        payload: {
          taxType: obligation.taxType,
          frequency: obligation.cadenceMismatch,
          effectiveDate: new Date(effectiveDate).toISOString(),
          // The configured label at record time IS the old regime — capture it so the
          // history grid keeps rendering the pre-change months correctly after the
          // picklist is later updated to the new frequency.
          previousFrequency: obligation.filingFrequencyLabel ?? undefined,
          note: note.trim() || undefined,
        },
      },
      { onSuccess: () => setRecordOpen(false) },
    );
  }

  const accessLabel =
    obligation.hasDirectAccess === true
      ? t('clientDetailPage.obligations.directAccessYes')
      : obligation.hasDirectAccess === false
      ? t('clientDetailPage.obligations.directAccessNo')
      : t('clientDetailPage.obligations.directAccessUnknown');

  const rowClasses = [
    obligation.source === 'derived' ? styles.derivedRow : undefined,
    onClick ? styles.clickableRow : undefined,
  ].filter(Boolean).join(' ');

  return (
    <TableRow
      className={rowClasses || undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
    >
      <TableCell>
        <TableCellLayout
          description={
            obligation.hasCountySurtax ? (
              // The rate this row's filings compute at, resolved for the obligation that
              // generates them. With no single generating obligation, the client default.
              <ObligationSurtaxLine
                clientId={stateTaxClientId}
                obligationId={obligation.filingObligationId}
              />
            ) : undefined
          }
        >
          {obligation.taxType}
        </TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>{obligation.stateLabel ?? dash}</TableCellLayout>
      </TableCell>
      <TableCell>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
          <span>
            {obligation.filingFrequencyLabel
              ? cadenceName(t, obligation.filingFrequencyLabel)
              : dash}
          </span>
          {frequencyChange && (
            <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
              {t('clientDetailPage.obligations.frequencySince', {
                frequency: frequencyChange.frequency,
                date: formatWholeDay(frequencyChange.effectiveDate),
              })}
            </Text>
          )}
          {showMismatch && (
            <Popover open={recordOpen} onOpenChange={(_e, d) => setRecordOpen(d.open)} positioning="below-start">
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  size="small"
                  appearance="outline"
                  onClick={(e) => e.stopPropagation()}
                  title={t('clientDetailPage.obligations.cadenceMismatch', { observed: obligation.cadenceMismatch })}
                >
                  ⚠ {t('clientDetailPage.obligations.cadenceMismatch', { observed: obligation.cadenceMismatch })}
                </Button>
              </PopoverTrigger>
              <PopoverSurface>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '240px' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Text weight="semibold">
                    {t('clientDetailPage.obligations.recordChangeTitle', {
                      frequency: obligation.cadenceMismatch,
                    })}
                  </Text>
                  <label style={{ fontSize: tokens.fontSizeBase200 }}>
                    {t('clientDetailPage.obligations.effectiveDate')}
                    <CalendarDateInput value={effectiveDate} onChange={setEffectiveDate} />
                  </label>
                  <Input
                    placeholder={t('clientDetailPage.obligations.noteOptional')}
                    value={note}
                    onChange={(_e, d) => setNote(d.value)}
                  />
                  <Button
                    appearance="primary"
                    size="small"
                    disabled={recordMutation.isPending || !effectiveDate}
                    onClick={submitRecord}
                  >
                    {t('clientDetailPage.obligations.recordChange')}
                  </Button>
                </div>
              </PopoverSurface>
            </Popover>
          )}
        </div>
      </TableCell>
      <TableCell>
        <TableCellLayout>{obligation.informationSourceLabel ?? dash}</TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>{accessLabel}</TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>{formatReportingSince(obligation, dash)}</TableCellLayout>
      </TableCell>
      {/* Stop clicks inside the filing-history grid from bubbling to the row's
          edit handler — the squares open their own period popover; dead space
          in this cell must not trigger "Edit obligation". */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <FilingHistoryStrip obligation={obligation} stateTaxClientId={stateTaxClientId} clientName={clientName} />
      </TableCell>
      <TableCell>
        <Text className={styles.notesCell}>{obligation.notes ?? dash}</Text>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          {/* F2: clicking the status badge opens a Popover showing the lifecycle
              audit trail. Mirrors FilingHistoryStrip's cell-as-trigger pattern. */}
          <Popover positioning="below-end">
            <PopoverTrigger disableButtonEnhancement>
              <button
                type="button"
                className={styles.statusBadgeButton}
                aria-label={t('clientDetailPage.obligationHistory.openAria')}
              >
                {obligation.effectiveEndDate ? (
                  // Ended/cancelled with an effective date — visually distinct from
                  // active/inactive. Dataverse rows report no lifecycleStatus → "Ended".
                  // A future effective date reads as "Ends {date}" (not yet ended); past/today
                  // reads as "Ended {date}". Cancellations keep "Cancelled {date}" either way.
                  <Badge appearance="filled" color="warning">
                    {t(
                      obligation.lifecycleStatus === 'cancelled'
                        ? 'clientDetailPage.obligations.cancelledOn'
                        : obligation.effectiveEndDate.slice(0, 10) > todayLocalIso()
                          ? 'clientDetailPage.obligations.endsOn'
                          : 'clientDetailPage.obligations.endedOn',
                      { date: formatWholeDay(obligation.effectiveEndDate) },
                    )}
                  </Badge>
                ) : (
                  <Badge appearance="filled" color={obligation.active ? 'success' : 'subtle'}>
                    {obligation.active
                      ? t('clientDetailPage.obligations.active')
                      : t('clientDetailPage.obligations.inactive')}
                  </Badge>
                )}
              </button>
            </PopoverTrigger>
            <PopoverSurface aria-label={t('clientDetailPage.obligationHistory.title')}>
              <ObligationHistoryPopover
                stateTaxClientId={stateTaxClientId}
                obligationId={obligation.id}
              />
            </PopoverSurface>
          </Popover>
        </TableCellLayout>
      </TableCell>
      {/* Trailing actions menu (End/cancel with a date · Remove). stopPropagation keeps the
          whole-row click-to-edit from firing; derived rows get an empty cell (no backing
          record). aria-label on a plain icon Button is fine (the Fluent gotcha is controlled
          Dropdowns only; a Menu trigger is not one). */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        {(onEnd || onRemove || onReactivate || onEditSurtax) && (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                icon={<MoreHorizontalRegular />}
                aria-label={t('clientDetailPage.obligations.actionsAria')}
                onClick={(e) => e.stopPropagation()}
              />
            </MenuTrigger>
            <MenuPopover onClick={(e) => e.stopPropagation()}>
              <MenuList>
                {onEditSurtax && (
                  <MenuItem onClick={() => onEditSurtax()}>
                    {t('clientDetailPage.obligations.editSurtaxMenuItem')}
                  </MenuItem>
                )}
                {onReactivate && (
                  <MenuItem onClick={() => onReactivate()}>
                    {t('clientDetailPage.obligations.reactivateMenuItem')}
                  </MenuItem>
                )}
                {onEnd && (
                  <MenuItem onClick={() => onEnd()}>
                    {t('clientDetailPage.obligations.endMenuItem')}
                  </MenuItem>
                )}
                {onRemove && (
                  <MenuItem onClick={() => onRemove()}>
                    {t('clientDetailPage.obligations.removeMenuItem')}
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </TableCell>
    </TableRow>
  );
}
